import {Injectable} from '@angular/core';
import {Subject} from 'rxjs';

import {HsLanguageService} from 'hslayers-ng';

import attractivenessConfig from '../attractiveness.config.json';
import {AdjusterEventService} from './adjuster-event.service';
import {RDFSubject} from './ontology.model';

export type Label = {
  '@value': string;
  '@language'?: string;
};

export type Dataset = {
  id: string;
  labels: Label[];
  desc: string;
  included: boolean;
};

export type DatasetGroup = {
  id: string;
  labels?: Label[];
};

export type Factor = {
  id: string;
  labels: Label[];
  weight: number;
  datasets: Dataset[];
};

export type Problem = {
  id: string;
  labels?: Label[];
  requiredDatasets: string[];
};

export type Role = {
  id: string;
  labels?: Label[];
  problems?: Problem[];
};

export type Schema = {
  id: string;
  labels?: Label[];
  groups?: DatasetGroup[];
};

@Injectable({providedIn: 'root'})
export class AdjusterPresetsService {
  /** To be read from a config file */
  serviceBaseUrl: string;

  activeProblem: Problem;
  activeRole: Role;
  activeSchema: Schema;
  applicationLoD = 'http://www.semanticweb.org/attractiveness/NUTS3' as const;
  applicationCoverage =
    'http://www.semanticweb.org/attractiveness/Europe' as const;
  defaultSchemaId: string;
  enabledRoleIds: Array<string>;
  enabledSchemaIds: Array<string>;

  schemaChanges: Subject<Schema> = new Subject();
  problemChanges: Subject<Problem> = new Subject();

  ontology: RDFSubject[];
  roles: Role[] = [];
  schemas: Schema[] = [];

  constructor(
    public adjusterEventService: AdjusterEventService,
    private hsLanguageService: HsLanguageService
  ) {
    this.serviceBaseUrl =
      attractivenessConfig?.serviceBaseUrl ??
      'https://publish.lesprojekt.cz/nodejs/';
    // 'https://jmacura.ml/ws/' // 'http://localhost:3000/'
    this.enabledRoleIds = attractivenessConfig?.enabledRoles ?? [];
    this.enabledSchemaIds = attractivenessConfig?.enabledSchemas ?? [];
    this.defaultSchemaId =
      attractivenessConfig?.defaultClassificationSchema ?? '';
    this.adjusterEventService.ontologyLoads.subscribe((ontology) => {
      this.ontology = ontology;
      this.loadRoles();
      this.loadSchemas();
      this.problemChanges.next(this.activeProblem ?? null);
    });
  }

  applyProblem(problem: Problem) {
    this.problemChanges.next(problem);
  }

  changeSchema(schema: Schema) {
    this.schemaChanges.next(schema);
  }

  getActiveRoleProblems(): Array<Problem> {
    return this.activeRole?.problems ?? [];
  }

  getActiveSchemaGroups(): Array<DatasetGroup> {
    return this.activeSchema.groups ?? [];
  }

  getGroupDatasets(groupID: string): Array<Dataset> {
    return (
      this.ontology
        .find((subject) => subject['@id'] == groupID)
        ['http://www.semanticweb.org/attractiveness/hasDataset']?.map(
          (entity) => {
            const datasetEntity = this.ontology.find(
              (subject) => subject['@id'] == entity['@id']
            );
            if (
              datasetEntity[
                'http://www.semanticweb.org/attractiveness/hasCoverage'
              ]?.every(
                (coverageEntity) =>
                  coverageEntity['@id'] !== this.applicationCoverage
              ) ||
              datasetEntity[
                'http://www.semanticweb.org/attractiveness/hasLoD'
              ]?.every((lodEntity) => lodEntity['@id'] !== this.applicationLoD)
            ) {
              return;
            }
            return {
              id: datasetEntity['@id'],
              labels:
                datasetEntity['http://www.w3.org/2000/01/rdf-schema#label'],
              desc: datasetEntity[
                'http://www.semanticweb.org/attractiveness#description'
              ]?.[0]['@value'], //so far descriptions are only written in en
              included: true,
            };
          }
        )
        .filter((ds) => ds) ?? []
    );
  }

  getLabelInCurrentLang(labels: Label[]) {
    if (!labels?.length) {
      return;
    }
    return this.getLabelInLang(
      labels,
      this.hsLanguageService.getCurrentLanguageCode('default')
    );
  }

  getLabelInLang(labels: Label[], lang: string) {
    return (
      labels.find((labelEntity) => labelEntity['@language'] == lang)?.[
        '@value'
      ] ?? labels[0]['@value']
    );
  }

  loadRoles() {
    this.roles = this.ontology
      .filter((subject) =>
        subject['@type']?.includes(
          'http://www.semanticweb.org/attractiveness/Role'
        )
      )
      .map((subject) => {
        return {
          id: subject['@id'],
          labels: subject['http://www.w3.org/2000/01/rdf-schema#label'],
          problems: subject[
            'http://www.semanticweb.org/attractiveness/solvesProblem'
          ].map((problem) => {
            const problemEntity = this.ontology.find(
              (subject) => subject['@id'] == problem['@id']
            );
            return {
              id: problem['@id'],
              labels:
                problemEntity?.['http://www.w3.org/2000/01/rdf-schema#label'],
              requiredDatasets: problemEntity?.[
                'http://www.semanticweb.org/attractiveness/requiresDataset'
              ].map((dataset) => dataset['@id']),
            };
          }),
        };
      });
    if (this.enabledRoleIds) {
      this.roles = this.roles.filter((role) =>
        this.enabledRoleIds.includes(role.id)
      );
    }
    this.activeRole = this.roles[0];
    this.activeProblem = this.roles[0].problems[0];
  }

  loadSchemas() {
    this.schemas = this.ontology
      .filter((subject) =>
        subject['@type']?.includes(
          'http://www.semanticweb.org/attractiveness/ClassificationSchema'
        )
      )
      .map((subject) => {
        return {
          id: subject['@id'],
          labels: subject['http://www.w3.org/2000/01/rdf-schema#label'],
          groups: subject[
            'http://www.semanticweb.org/attractiveness/consistsOf'
          ].map((group) => {
            return {
              id: group['@id'],
              labels: this.ontology.find(
                (subject) => subject['@id'] == group['@id']
              )?.['http://www.w3.org/2000/01/rdf-schema#label'],
            };
          }),
        };
      });
    if (this.enabledSchemaIds) {
      this.schemas = this.schemas.filter((schema) =>
        this.enabledSchemaIds.includes(schema.id)
      );
    }
    this.activeSchema =
      this.schemas.find((schema) => schema.id == this.defaultSchemaId) ??
      this.schemas[0];
    for (const group of this.activeSchema.groups) {
      const ds = this.ontology.find((subject) => subject['@id'] == group.id)[
        'http://www.semanticweb.org/attractiveness/hasDataset'
      ];
    }
    this.schemaChanges.next(this.activeSchema);
  }

  pickProblem(role) {
    this.activeProblem = role.problems[0] ?? null;
    this.applyProblem(this.activeProblem);
  }
}
